import {
	Button,
	Colors,
	ConfirmationModal,
	DialogContainer,
	Modal,
	ModalFooter,
	ModalHeading,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	ModelSubHeading,
	TextFieldWrapper,
} from "@factorearth/component-library";
import DrawerTutorialButton from "components/DrawerTutorial/DrawerTutorialButton/DrawerTutorialButton";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import FormattedActionBar from "components/FormattedActionBar/FormattedActionBar";
import { FieldTemplate, FormTemplate, FormTemplateForResolver, Option, User } from "db/definitions";
import { createFieldTemplateForBatchResolver } from "db/queries/fieldTemplateQueries";
import { useTheme } from "hooks/useSingleton";
import { useTemplateManager } from "hooks/useTemplateManager";
import React, { useEffect, useState } from "react";
import { FiChevronLeft, FiEdit3, FiFileText, FiPlus, FiSave, FiX } from "react-icons/fi";
import { useNavigate } from "react-router";
import enforceFieldTemplateDoc from "utils/enforceFieldTemplateDoc";
import getFullFormTemplate from "utils/getFullFormTemplate";
import reorder from "utils/reorder";
import { v4 as uuid } from "uuid";
import EditFieldTemplate from "../EditFieldTemplate/EditFieldTemplate";
import UpdateTemplateForm from "../UpdateTemplateForm/UpdateTemplateForm";
import styles from "./EditFormTemplate.module.scss";
import { picklistTypes } from "assets/json/global";
import { Location } from "hooks/useDrawer";
import useCustomForm from "hooks/useCustomForm";
import { recordSentry } from "utils/recordSentry";
import { describeDuplicateDocIds, findDuplicateDocIds } from "utils/findDuplicateDocIds";

interface ConfirmPublishInterface {
	action: "CONFIRM_PUBLISH";
	cb: (bool: boolean) => void;
	canPublish: boolean;
}

interface ConfirmDeleteInterface {
	cb: () => void;
	docType: string;
}

interface ConfirmNewVersionInterface {
	action: "CONFIRM_NEW_VERSION";
	cb: ({ newVersionName, publish }: { newVersionName: string; publish: boolean }) => void;
	canPublish: boolean;
}

interface Props {
	addFormTemplateToListInState: (newTemplate: FormTemplateForResolver) => void;
	closeEditTemplate: () => void;
	online: boolean;
	setupDrawer: (location: "" | Location) => void;
	userDoc: User;
}

function EditFormTemplate(props: Props) {
	const { addFormTemplateToListInState, closeEditTemplate, online, setupDrawer, userDoc } = props;
	const [fields, setFields] = useState<Record<string, FieldTemplate>>({});
	const [loaded, setLoaded] = useState(false);
	const [open, setOpen] = useState(false);
	const [showModal, setShowModal] = useState<null | ConfirmPublishInterface | ConfirmNewVersionInterface>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState<null | ConfirmDeleteInterface>(null);
	// This is just going to have to be the solution to track what are new fields, so we can skip the new version if its a new field.
	// (It will also skip new version if the template is not published, but this is for when an update is required and a new field needs to be added to taht)
	const [newFieldIds, setNewFieldIds] = useState<Record<string, FieldTemplate>>({});

	// This will either be the new version number (uuid_timestamp) or null
	const [newVersionRequired, setNewVersionRequired] = useState("");
	const [docIdWarning, setDocIdWarning] = useState<string | null>(null);
	const [docIdWarningOpen, setDocIdWarningOpen] = useState(false);
	const [editFields, setEditFields] = useState(false);
	const [fieldHtml, setFieldHtml] = useState<React.JSX.Element[]>([]);

	const color = useTheme();
	const { createFormTemplate, updateFieldTemplates, updateFormTemplate, setTemplateToEdit, templateToEdit: formTemplate } = useTemplateManager();
	if (!formTemplate) return <div>No Template Selected</div>;
	const [template, setTemplate] = useState(formTemplate);

	const { values, handleChange, handleSubmit, handleUpdateMultiplePropsAtOnce } = useCustomForm(template, handleFormTemplateUpdate);

	useEffect(() => {
		setTemplate(formTemplate);
	}, [formTemplate]);

	/**
	 * initial setup
	 */
	useEffect(() => {
		async function getTheFullFormTemplate(id: string) {
			const fullFormTemplate = await getFullFormTemplate(id, online);
			if (fullFormTemplate) setTemplate(fullFormTemplate);
		}
		if (formTemplate?.fieldTemplates?.nextToken) {
			setLoaded(false);
			getTheFullFormTemplate(formTemplate.id);
		} else {
			setLoaded(true);
		}
	}, [online]);

	/**
	 * this is to trigger re-renders based on specific effects.
	 */
	useEffect(() => {
		if (template?.fieldTemplates && template?.fieldsOrder) {
			setFields(mapFields(template.fieldTemplates.items, template?.fieldsOrder));
		}
	}, [template?.fieldTemplates?.items, template?.fieldsOrder]);

	useEffect(() => {
		if (fields) {
			setLoaded(false);
			// fields is null if its not set up yet
			renderFields(fields);
			setLoaded(true);
		}
	}, [fields, template]);

	/**
	 * Maps a form templates fields to an object to be updated and eventually submitted to db
	 * at the end in batches.  The idea is to normalize this flow so both edit and new
	 * are using this
	 * @param fieldsArray of the templates fields
	 */
	function mapFields(fieldsArray: FieldTemplate[], orderArray: string[]) {
		const fieldMap: Record<string, FieldTemplate> = {};

		for (let i = 0; i < fieldsArray.length; i++) {
			const fieldToMap = fieldsArray[i];
			fieldMap[fieldToMap.id] = fieldToMap;
		}

		const sortedArray = orderArray.map((id) => fieldMap[id]).filter((field) => field !== undefined);

		const sortedFieldMap = sortedArray.reduce(
			(map, field) => {
				map[field.id] = field;
				return map;
			},
			{} as Record<string, FieldTemplate>,
		);

		return sortedFieldMap;
	}

	function handleRearrangeField(start: number, end: number) {
		if (!template) return;
		const copy = { ...template };
		if (!copy.fieldsOrder) return;
		const newFieldsOrder = reorder(copy.fieldsOrder, start, end);
		copy.fieldsOrder = newFieldsOrder;
		setTemplate(copy);
	}

	/**
	 * Builds the html for the dom based on the fields and template.fieldsOrder
	 * @returns html
	 */
	function renderFields(fieldsMap: Record<string, FieldTemplate>) {
		if (!template?.fieldsOrder) return;
		const final = [];
		const keys = template.fieldsOrder;
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i];
			const field = fieldsMap[key];
			if (!field) {
				continue;
			}
			const isLast = i === keys.length - 1;
			// going to enforce the document here, so docs that have null properties still show up in the
			// updateformtemplate component.
			const fullField = enforceFieldTemplateDoc(field);

			const html = (
				<EditFieldTemplate
					fieldTemplate={fullField}
					handleRearrangeField={handleRearrangeField}
					handleRemoveFieldTemplate={handleRemoveFieldTemplate}
					handleShowDeleteModal={handleShowDeleteModal}
					index={i}
					isLast={isLast}
					key={`edit-field-template-wrapper-${fullField.id}`}
					online={online}
					setupDrawer={setupDrawer}
					updateFieldTemplate={handleUpdateField}
					userDoc={userDoc}
				/>
			);

			final.push(html);
		}
		setFieldHtml(final);
	}

	function handleRemoveFieldTemplate(id: string) {
		if (!template) return;
		const templateCopy: FormTemplate = { ...template };
		if (!templateCopy.fieldTemplates || !templateCopy.fieldsOrder) return;
		const fieldIdsCopy = { ...newFieldIds };
		const fieldTemplatesCopy = templateCopy.fieldTemplates.items.filter((doc) => doc.id !== id);
		const fieldsOrder = templateCopy.fieldsOrder.filter((doc) => doc !== id);
		const fieldsCopy = { ...fields };
		delete fieldsCopy[id];
		templateCopy.fieldsOrder = fieldsOrder;
		templateCopy.fieldTemplates.items = fieldTemplatesCopy;

		setNewFieldIds(fieldIdsCopy);
		setFields(fieldsCopy);
		setTemplate(templateCopy);
	}

	/**
	 * Adds a new field to the form template at the specified position.
	 * @param position number of the index to add the new field at.
	 * @returns void
	 */
	function handleAddNewFieldAtPosition(position: number) {
		if (!template) return;
		const templateCopy: FormTemplate = { ...template };
		if (!templateCopy.fieldTemplates || !templateCopy.fieldsOrder) return;
		const fieldIdsCopy = { ...newFieldIds };
		const newFieldObj = createNewField();
		if (!newFieldObj) return;
		fieldIdsCopy[newFieldObj.id] = newFieldObj;
		if (position > -1 && position < templateCopy.fieldsOrder.length + 1) {
			templateCopy.fieldTemplates.items.splice(position, 0, newFieldObj);
			templateCopy.fieldsOrder.splice(position, 0, newFieldObj.id);
		} else {
			recordSentry({ error: "error", msg: "Invalid position to add new field at", additionalData: { position, fieldsOrder: templateCopy.fieldsOrder } });
		}
		const fieldsCopy = { ...fields };
		fieldsCopy[newFieldObj.id] = newFieldObj;
		setNewFieldIds(fieldIdsCopy);
		setFields(fieldsCopy);
		setTemplate(templateCopy);
	}

	/**
	 * builds the html for a new field, defaults to text type.
	 * @returns newFieldObj. This has the new field data.
	 */
	function createNewField() {
		if (!template || !formTemplate) return null;
		const newFieldTemplateValues: FieldTemplate = {
			header: "New Field-" + Date.now(),
			id: uuid(),
			formTemplate: template.id,
			inputType: "text",
			exportType: "text",
			exportActions: [],
			rowOrder: [],
			tableFields: { items: [] },
			options: { items: [] },
			optionsOrder: [],
			action: [],
			actionData: [],
			exportData: [],
			docId: "",
			version: formTemplate.version,
			csvKey: "",
			identityRole: "",
			group: userDoc.group,
			readGroups: userDoc.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [],
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		};

		const fullFieldTemplate = enforceFieldTemplateDoc(newFieldTemplateValues);
		return fullFieldTemplate;
	}

	/**
	 * This is the callback that will get called on Blur in the UpdateTemplateForm component.
	 * @param data that needs to be updated and added onto the form template when save occurs.
	 */
	async function handleFormTemplateUpdate(item: FormTemplate) {
		setTemplate(item);
	}

	/**
	 * Callback when a user is ready to save the form template in its entirety.
	 * This will add on the props that are excluded from the UpdateTemplateForm (i.e. fieldsOrder)
	 * So those will be managed separately in this component. -- this is pretty Jank, but if it works
	 * @param {FormTemplate} templateData from the UpdateTemplateForm component.  We need to add on the props
	 * that are not being handled by UpdateTemplateForm.
	 */
	function submitTemplate({ templateData, newVersionName, publish }: { templateData: FormTemplate; newVersionName?: string; publish?: boolean }) {
		if (!template) return;
		const final: FormTemplate = { ...template, ...templateData, fieldsOrder: template.fieldsOrder };
		if (final.publish === undefined) final.publish = false;
		if (!final.fieldsOrder || !final.fieldTemplates || !final.id) return;

		if (newVersionRequired && templateData.publish) {
			final.version = newVersionRequired;
			final.versionName = newVersionName;
			final.id = uuid();
			final.publish = publish;
		}

		if (publish !== null && publish !== undefined && !final.publish) {
			final.publish = publish;
		}
		const enforcedFinalObj = enforceVersionsAndIds(
			final as Required<Pick<FormTemplate, "fieldsOrder" | "fieldTemplates" | "id" | "publish">> & FormTemplate,
		);
		const enforcedFinal = enforcedFinalObj.finalFormTemplate;
		if (!enforcedFinal.fieldsOrder) return;
		enforcedFinal.updatedBy = userDoc.username;
		enforcedFinal.updatedAt = new Date().toISOString();
		// @ts-ignore
		if (templateData.new || newVersionRequired) {
			createFormTemplate(enforcedFinal);
		} else {
			updateFormTemplate(enforcedFinal);
		}

		setTemplate(enforcedFinal);
		updateFieldTemplates(
			enforcedFinalObj.finalFields,
			enforcedFinalObj.finalOptionsArray,
			enforcedFinalObj.finalTableFieldsMap,
			enforcedFinal.id,
			enforcedFinal.fieldsOrder,
		);

		addFormTemplateToListInState(enforcedFinal);
		setShowModal(null);
		setTemplateToEdit(null);
	}

	interface EnforceVersionsAndIdsReturn {
		finalFormTemplate: FormTemplate;
		finalFields: Record<string, FieldTemplate>;
		finalOptionsArray: Option[];
		finalTableFieldsMap: Record<string, FieldTemplate>;
	}

	/**
	 * Setup with new ids and everything to be created in the cloud properly.
	 * This will also add in versionNumbers if its missing.
	 * @param templateToEnforce the form templates that has required a version change that needs
	 */
	function enforceVersionsAndIds(
		templateToEnforce: Required<Pick<FormTemplate, "fieldsOrder" | "fieldTemplates" | "id" | "publish">> & FormTemplate,
	): EnforceVersionsAndIdsReturn {
		// first setup a map to easily deal with the field template list and the fieldsOrder etcc.
		const fieldTemplateMap: Record<string, FieldTemplate> = {};
		const tableFieldMap: Record<string, FieldTemplate[]> = {};
		const finalFormTemplate: FormTemplate = { ...templateToEnforce };
		let finalFieldsArray = Object.values(fields);
		const finalOptionsArray: Option[] = [];
		const fieldTemplateToOptionsMap: Record<string, Option[]> = {};
		const finalTableFieldsMap: Record<string, FieldTemplate> = {};
		for (let i = 0; i < templateToEnforce.fieldTemplates.items.length; i++) {
			const fieldTemplateToEnforce = templateToEnforce.fieldTemplates.items[i];
			fieldTemplateToEnforce.updatedAt = new Date().toISOString();
			const batchEnforcedFieldTemplate = createFieldTemplateForBatchResolver(fieldTemplateToEnforce);
			if (fieldTemplateToEnforce.options && fieldTemplateToEnforce.options.items.length > 0) {
				fieldTemplateToOptionsMap[fieldTemplateToEnforce.id] = fieldTemplateToEnforce.options.items;
			}

			fieldTemplateMap[fieldTemplateToEnforce.id] = batchEnforcedFieldTemplate;

			if (fieldTemplateToEnforce.tableFields && fieldTemplateToEnforce.tableFields.items.length > 0) {
				tableFieldMap[fieldTemplateToEnforce.id] = fieldTemplateToEnforce.tableFields.items;
			}
		}
		if (templateToEnforce.publish) {
			if (newVersionRequired) {
				// its an existing one, so need to update all the ids for all the field templates and the versionNumber.
				// also need to update the formTemplateIds for all of the fields
				// Then fix up the fieldsOrder for the parent
				const newFieldsOrder = [];
				const newTemplates = [];
				// loop through in order
				for (let i = 0; i < templateToEnforce.fieldsOrder.length; i++) {
					const newId = uuid();
					const orderedTemplateId = templateToEnforce.fieldsOrder[i];
					const fieldTemplate = fieldTemplateMap[orderedTemplateId];
					fieldTemplate.id = newId;
					fieldTemplate.version = newVersionRequired;
					fieldTemplate.formTemplate = templateToEnforce.id;
					const tableFields = tableFieldMap[orderedTemplateId];
					if (fieldTemplateToOptionsMap[orderedTemplateId]) {
						const optionsByIdMap: Record<string, Option> = {};
						for (const option of fieldTemplateToOptionsMap[orderedTemplateId]) {
							optionsByIdMap[option.id] = option;
						}
						const newOptionsOrder: string[] = [];
						const optionsOrder = fieldTemplate.optionsOrder ?? [];
						const orderToUse: string[] =
							optionsOrder.length > 0 ? optionsOrder : fieldTemplateToOptionsMap[orderedTemplateId].map((o: Option) => o.id);
						for (const optionId of orderToUse) {
							const option = optionsByIdMap[optionId];
							if (!option) continue;
							const newOption: Option = { ...option, id: uuid(), fieldTemplateId: newId, formTemplate: templateToEnforce.id };
							finalOptionsArray.push(newOption);
							newOptionsOrder.push(newOption.id);
						}
						fieldTemplate.optionsOrder = newOptionsOrder;
					}
					if (tableFields) {
						let newRowOrder = [];
						const tableFieldsById: Record<string, FieldTemplate> = {};
						for (const cell of tableFields) {
							tableFieldsById[cell.id] = cell;
						}
						const cellRowOrder =
							fieldTemplate.rowOrder && fieldTemplate.rowOrder.length > 0 ? fieldTemplate.rowOrder : tableFields.map((f) => f.id);
						for (const cellId of cellRowOrder) {
							const cellField = tableFieldsById[cellId];
							if (!cellField) continue;
							const newCellId = uuid();
							cellField.id = newCellId;
							cellField.version = newVersionRequired;
							cellField.formTemplate = templateToEnforce.id;
							cellField.tableField = newId;
							newRowOrder.push(cellField.id);
							finalTableFieldsMap[cellField.id] = cellField;
							if (cellField.options && cellField.options.items.length > 0) {
								const cellOptionsByIdMap: Record<string, Option> = {};
								for (const option of cellField.options.items) {
									cellOptionsByIdMap[option.id] = option;
								}
								const newOptionsOrder: string[] = [];
								const cellOrderToUse =
									cellField.optionsOrder && cellField.optionsOrder.length > 0
										? cellField.optionsOrder
										: cellField.options.items.map((o: Option) => o.id);
								for (const optionId of cellOrderToUse) {
									const option = cellOptionsByIdMap[optionId];
									if (!option) continue;
									const newOption: Option = { ...option, id: uuid(), fieldTemplateId: newCellId, formTemplate: templateToEnforce.id };
									finalOptionsArray.push(newOption);
									newOptionsOrder.push(newOption.id);
								}
								cellField.optionsOrder = newOptionsOrder;
							}
						}

						fieldTemplate.rowOrder = newRowOrder;
					}

					newFieldsOrder.push(newId);
					newTemplates.push(fieldTemplate);
				}

				finalFormTemplate.fieldsOrder = newFieldsOrder;
				finalFieldsArray = newTemplates;
			} else {
				for (let i = 0; i < templateToEnforce.fieldsOrder.length; i++) {
					const orderedTemplateId = templateToEnforce.fieldsOrder[i];
					const tableFields = tableFieldMap[orderedTemplateId];
					if (fieldTemplateToOptionsMap[orderedTemplateId]) {
						for (const option of fieldTemplateToOptionsMap[orderedTemplateId]) {
							finalOptionsArray.push(option);
						}
					}
					if (tableFields) {
						for (let i = 0; i < tableFields.length; i++) {
							const cellField = JSON.parse(JSON.stringify(tableFields[i]));
							finalTableFieldsMap[cellField.id] = cellField;
							if (cellField.options && cellField.options.items.length > 0) {
								for (const option of cellField.options.items) {
									finalOptionsArray.push(option);
								}
							}
						}
					}
				}
			}
		} else {
			// NEED TO MAKE SURE ALL THE TABLE FIELDS ARE GETTING PUT IN HERE
			// loop through in order
			for (let i = 0; i < templateToEnforce.fieldsOrder.length; i++) {
				const orderedTemplateId = templateToEnforce.fieldsOrder[i];
				if (fieldTemplateToOptionsMap[orderedTemplateId]) {
					for (const option of fieldTemplateToOptionsMap[orderedTemplateId]) {
						finalOptionsArray.push(option);
					}
				}
				const tableFields = tableFieldMap[orderedTemplateId];
				if (tableFields) {
					for (let i = 0; i < tableFields.length; i++) {
						const cellField = JSON.parse(JSON.stringify(tableFields[i]));
						finalTableFieldsMap[cellField.id] = cellField;
						if (cellField.options && cellField.options.items.length > 0) {
							for (const option of cellField.options.items) {
								finalOptionsArray.push(option);
							}
						}
					}
				}
			}
		}

		const finalFields: Record<string, FieldTemplate> = {};
		for (let i = 0; i < finalFieldsArray.length; i++) {
			const fieldInArray = finalFieldsArray[i];
			fieldInArray.updatedAt = new Date().toISOString();
			finalFields[fieldInArray.id] = fieldInArray;
		}

		// @ts-ignore
		if (finalFormTemplate.new) delete finalFormTemplate.new;

		delete finalFormTemplate.fieldTemplates;
		return {
			finalFormTemplate,
			finalFields,
			finalOptionsArray,
			finalTableFieldsMap,
		};
	}

	/**
	 * Setup an overlay to confirm if a user is saving or publishing and saving.  Once its published
	 * It will just be saved.
	 */
	function submitTemplateClick(templateData: FormTemplate) {
		// Two fields (or two columns of one table) that share a docId write to the same slot in the export document, so
		// the export silently loses one of them. Warn, but do not block: the docIds may be intentional for a template
		// that is never exported.
		const duplicateDocIds = findDuplicateDocIds(Object.values(fields));
		if (duplicateDocIds.length > 0) {
			setDocIdWarning(`This template will export incorrectly until these are fixed. ${describeDuplicateDocIds(duplicateDocIds)}.`);
			setDocIdWarningOpen(true);
		}
		let overlayObj: ConfirmPublishInterface | ConfirmNewVersionInterface;
		// @ts-ignore
		if (templateData.new) {
			overlayObj = {
				action: "CONFIRM_PUBLISH",
				cb: (publish: boolean) => submitTemplate({ templateData, publish }),
				canPublish: true,
			};
		} else if (newVersionRequired && templateData.publish) {
			overlayObj = {
				action: "CONFIRM_NEW_VERSION",
				canPublish: true,
				cb: ({ newVersionName, publish }: { newVersionName: string; publish: boolean }) => submitTemplate({ templateData, newVersionName, publish }),
			};
		} else if (!templateData.publish) {
			overlayObj = {
				action: "CONFIRM_PUBLISH",
				cb: (publish: boolean) => submitTemplate({ templateData, publish }),
				canPublish: true,
			};
		} else {
			// If this is hit, it means it is already published and does not require a new version.
			overlayObj = {
				action: "CONFIRM_PUBLISH",
				cb: () => submitTemplate({ templateData }),
				canPublish: false,
			};
		}
		setShowModal(overlayObj);
	}

	/**
	 * updates a specific field template in state.
	 * @param item is the updated field template data. FieldTemplate
	 */
	async function handleUpdateField(item: FieldTemplate) {
		if (!template) return;
		const dataCopy: FieldTemplate = { ...item };
		const fieldsCopy: Record<string, FieldTemplate> = { ...fields };
		fieldsCopy[dataCopy.id] = dataCopy;
		const copyOfTemplate: FormTemplate = { ...template };
		if (!copyOfTemplate.fieldTemplates) return;
		for (let i = 0; i < copyOfTemplate.fieldTemplates.items.length; i++) {
			const fieldTemplate = copyOfTemplate.fieldTemplates.items[i];
			if (fieldTemplate.id === dataCopy.id) {
				// found the field in the copy - Need to compare the original field template to the updated one and verify the type changes are ok.
				if (determineNewVersionRequired(fieldTemplate, dataCopy)) {
					// THIS IS WHERE THE MAGIC NEEDS TO HAPPEN
					// New field type requires a new version
					// first check if its a new field for this round (potentially not in the original formTEmplate)
					// check if there is a new version number already
					if (newVersionRequired && !newFieldIds[dataCopy.id]) {
						// set the field template to use the new version number
						dataCopy.version = newVersionRequired;
					} else if (!newFieldIds[dataCopy.id]) {
						// there is no version number yet
						const versionNumber = uuid() + "_" + Date.now();
						setNewVersionRequired(versionNumber);
					}
				}
				copyOfTemplate.fieldTemplates.items[i] = dataCopy;
			}
		}

		setTemplate(copyOfTemplate);
		setFields(fieldsCopy);
	}

	/**
	 * Compares the original field template to the updated field template and determines if a new version is required.
	 * @param originalTemplate field template to be compared to the updated template
	 * @param updatedTemplate the new changes
	 * @returns true is a new version is required, false if not.
	 */
	function determineNewVersionRequired(originalTemplate: FieldTemplate, updatedTemplate: FieldTemplate) {
		if (!values.publish) return false;
		const originalType = originalTemplate.inputType;
		const newType = updatedTemplate.inputType;
		const originalIsPicklist = picklistTypes.includes(originalType);
		const newIsPicklist = picklistTypes.includes(newType);

		// Basically if one is a picklist and the other is not, we need a new version.
		return !(originalIsPicklist === newIsPicklist);
	}

	/**
	 * Generates and downloads a csv that can be used to batch create form groups.
	 */
	function generateCsv() {
		if (!formTemplate?.fieldTemplates || !formTemplate.fieldsOrder) return;
		const idArray = ["form_id", "form_group_name", formTemplate.id];
		const headerArray = ["", "", formTemplate.template];

		const templateMap: Record<string, FieldTemplate> = {};
		// for now just going to worry about non-table and non-image roll fields since those arent supported yet
		for (let i = 0; i < formTemplate.fieldTemplates.items.length; i++) {
			const fieldTemplate = formTemplate.fieldTemplates.items[i];
			templateMap[fieldTemplate.id] = fieldTemplate;
		}

		for (let i = 0; i < formTemplate.fieldsOrder.length; i++) {
			const fieldId = formTemplate.fieldsOrder[i];
			const fieldTemplate = templateMap[fieldId];
			idArray.push(fieldTemplate.id);
			let header = fieldTemplate.header;
			if (header.includes(",")) {
				header = `"${header}"`;
			}
			headerArray.push(header);
		}

		const idString = idArray.join(",");
		const headerString = headerArray.join(",");
		const csvString = idString + "\n" + headerString + "\n";
		const filename = formTemplate.template + "-csvupload-template.csv";
		const blob = new Blob([csvString]);
		const link = document.createElement("a");
		if (link.download !== undefined) {
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", filename);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}

	/**
	 * edit field toggle
	 */
	function toggleEditFields() {
		setEditFields(!editFields);
	}

	function toggleSaveForm() {
		submitTemplateClick(values);
		setOpen(!open);
	}

	function handleCloseEditTemplate() {
		closeEditTemplate();
	}

	function handleShowDeleteModal(cb: () => void, docType: string) {
		setShowDeleteModal({
			cb,
			docType,
		});
		setDeleteModalOpen(true);
	}

	if (!loaded) return <LoadingSpinner />;

	return (
		<>
			<ConfirmModal open={open} overlayObj={showModal} setOpen={setOpen} />
			<ConfirmDeleteModal open={deleteModalOpen} overlayObj={showDeleteModal} setOpen={setDeleteModalOpen} />
			{docIdWarning && (
				<ConfirmationModal
					colorPalette={color}
					details={docIdWarning}
					confirmTitle="Duplicate docIds"
					open={docIdWarningOpen}
					setOpen={setDocIdWarningOpen}
					toast="destructive"
					timeout={12000}
					canClose
				/>
			)}

			<div className={styles.wrapper}>
				{template?.fieldTemplates ? (
					<div
						className={styles.editFormTemplate_wrapper}
						key={template.id}
						style={{
							color: color.text.primary,
							background: color.background.primary,
						}}>
						{!editFields ? (
							<div className={styles.formTemplate_wrapper}>
								<div
									style={{
										color: color.text.primary,
										background: color.background.primary,
									}}
									className={styles.formTemplateHeader}>
									<div
										style={{
											color: color.text.primary,
											background: color.background.primary,
											display: "flex",
											gridGap: "10px",
										}}
										className={styles.top_header}>
										<CancelEditsButton
											button={<FiChevronLeft />}
											className={styles.header_back}
											colors={color}
											cancelCb={handleCloseEditTemplate}
										/>
										<div className={styles.header_text} data-test="edit-form-template-main-header">
											Edit {template.templateName || template.template} {template.versionName ? `- ${template.versionName}` : null}
										</div>
									</div>
									<FormattedActionBar color={color}>
										<div style={{ background: color.background.primary }} className={styles.left_w_space_between}>
											<CancelEditsButton
												button={
													<Button variant="outlined" colorPalette={color}>
														Back
													</Button>
												}
												cancelCb={handleCloseEditTemplate}
												colors={color}
											/>
											<Button colorPalette={color} onClick={toggleEditFields} variant="outlined">
												<span className={styles.react_icon} data-test="tm-edit-fields-btn">
													<FiEdit3 />
												</span>
												Edit Fields
											</Button>
										</div>
										<div className={styles.seperation_line} style={{ background: color.border.primary }} />
										<>
											<Button onClick={generateCsv} colorPalette={color} variant="outlined">
												<span className={styles.react_icon}>
													<FiFileText />{" "}
												</span>
												Generate CSV
											</Button>
										</>
										<div className={styles.seperation_line} style={{ background: color.border.primary }} />
										<Button onClick={toggleSaveForm} colorPalette={color} variant="filled">
											<span className={styles.react_icon} data-test="edit-form-save">
												<FiSave />
											</span>
											Save
										</Button>
										<CancelEditsButton
											button={
												<Button variant="destructive" colorPalette={color} onClick={closeEditTemplate}>
													Cancel
												</Button>
											}
											colors={color}
											cancelCb={closeEditTemplate}
										/>
										<div className={styles.seperation_line} style={{ background: color.border.primary }} />
										<DrawerTutorialButton
											helpColor={color.utils.success}
											location={"template-manager-edit-form-template"}
											setupDrawer={setupDrawer}
											style={{ border: "0px" }}
										/>
									</FormattedActionBar>
								</div>
								<UpdateTemplateForm
									handleUpdateField={handleUpdateField}
									template={template}
									docType={"FormTemplate"}
									submitCallback={handleFormTemplateUpdate}
									saveForm={open}
									submitClickCallback={submitTemplateClick}
									online={online}
									values={values}
									handleChange={handleChange}
									handleSubmit={handleSubmit}
									handleUpdateMultiplePropsAtOnce={handleUpdateMultiplePropsAtOnce}
								/>
							</div>
						) : (
							<div className={styles.fields_wrapper}>
								<div
									style={{
										color: color.text.primary,
										background: color.background.primary,
										display: "flex",
										position: "sticky",
										top: 0,
										zIndex: 2,
									}}
									className={styles.allFieldsHeader}>
									<div onClick={toggleEditFields} className={styles.header_back} style={{ paddingRight: "7px" }}>
										<FiChevronLeft />
									</div>
									<div className={styles.header_text}>
										{template.fieldTemplates.items.length > 0 ? `All Fields for ${template.template}` : "Add a Field"}
									</div>
								</div>
								<FormattedActionBar color={color}>
									<div style={{ background: color.background.primary }} className={styles.left_w_space_between}>
										<Button colorPalette={color} onClick={toggleEditFields} variant="outlined">
											<span className={styles.react_icon}>
												<FiChevronLeft />
											</span>
											<div data-test="edit-fields-back">Back</div>
										</Button>
										<Button colorPalette={color} onClick={toggleSaveForm} variant="filled">
											<span className={styles.react_icon}>
												<FiSave />
											</span>
											Save
										</Button>
									</div>
									<CancelEditsButton
										button={
											<Button variant="destructive" colorPalette={color}>
												Cancel
											</Button>
										}
										colors={color}
										cancelCb={toggleEditFields}
									/>
									<div className={styles.seperation_line} style={{ background: color.border.primary }} />
									<Button
										style={{ background: color.background.tertiary, color: color.text.primary }}
										className={styles.button}
										onClick={() => handleAddNewFieldAtPosition(0)}
										colorPalette={color}
										variant="filled">
										<span className={styles.react_icon}>
											<FiPlus />
										</span>
										Add Field To Beginning
									</Button>
									<DrawerTutorialButton
										helpColor={color.utils.success}
										location={"template-manager-edit-fields-main"}
										setupDrawer={setupDrawer}
										style={{ border: "0px" }}
									/>
								</FormattedActionBar>
								<div data-test="tm-fields-container" className={styles.fields_container}>
									{fieldHtml}
								</div>

								<div
									style={{
										background: color.background.primary,
										borderBottom: `1px solid ${color.border.primary}`,
										borderTop: `1px solid ${color.border.primary}`,
									}}
									className={styles.btn_wrapper}>
									<Button
										style={{ background: color.background.tertiary, color: color.text.primary }}
										className={styles.button}
										onClick={() => handleAddNewFieldAtPosition(template.fieldsOrder ? template.fieldsOrder.length : -1)}
										colorPalette={color}
										variant="filled">
										<span className={styles.react_icon} data-test="tm-add-new-field-btn">
											<FiPlus />
										</span>
										Add Field to End
									</Button>
								</div>
							</div>
						)}
					</div>
				) : (
					<div>Loading...</div>
				)}
			</div>
		</>
	);
}

interface CancelEditsButtonProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	button: React.JSX.Element;
	colors: Colors;
	cancelCb: () => void;
}

function CancelEditsButton(props: CancelEditsButtonProps) {
	const { button, colors, cancelCb, ...rest } = props;
	const [open, setOpen] = useState(false);

	function submit() {
		setOpen(false);
		cancelCb();
	}

	const clonedButton = React.cloneElement(button, {
		onClick: () => setOpen(true),
	});

	return (
		<div {...rest}>
			{clonedButton}
			<ConfirmationModal
				colorPalette={colors}
				setOpen={setOpen}
				confirmTitle="Exit Template"
				details="Are you sure you want to exit? This will discard all unsaved changes."
				handleSubmit={submit}
				timeout={5000}
				open={open}
				canClose
			/>
		</div>
	);
}

interface ConfirmModalProps {
	open: boolean;
	overlayObj: ConfirmNewVersionInterface | ConfirmPublishInterface | null;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function ConfirmModal(props: ConfirmModalProps) {
	const { open, overlayObj, setOpen } = props;
	const [versionName, setVersionName] = useState("");

	const color = useTheme();
	const navigate = useNavigate();

	function submit(publish: boolean) {
		if (!overlayObj) return;
		if (overlayObj.action === "CONFIRM_NEW_VERSION") {
			if (versionName) {
				navigate(`/template-manager`);
				overlayObj.cb({ newVersionName: versionName, publish });
			}
		} else if (overlayObj.action === "CONFIRM_PUBLISH") {
			navigate(`/template-manager`);
			overlayObj.cb(publish);
		}
		setOpen(false);
	}

	return (
		overlayObj && (
			<Modal open={open} colorPalette={color} setOpen={setOpen}>
				<DialogContainer style={{ zIndex: 10 }}>
					<ModalTitleContainer>
						<ModalTitle>Save FormTemplate</ModalTitle>
						<FiX onClick={() => setOpen(false)} size={24} />
					</ModalTitleContainer>
					{overlayObj.action === "CONFIRM_NEW_VERSION" && (
						<ModalInputBox>
							<ModalHeading>Please enter a name for the new version</ModalHeading>
							<TextFieldWrapper colorPalette={color} onChange={(e) => setVersionName(e.target.value)} value={versionName} />
						</ModalInputBox>
					)}
					<ModelSubHeading>Do you want to save{overlayObj.canPublish && " and/or publish"}?</ModelSubHeading>
					<ModalFooter>
						<Button
							colorPalette={color}
							variant="outlined"
							label={overlayObj.canPublish ? `Save and publish` : `Save`}
							onClick={() => submit(overlayObj.action === "CONFIRM_NEW_VERSION" ? overlayObj.canPublish : true)}
						/>
						{overlayObj.canPublish && (
							<Button colorPalette={color} variant="outlined" label="Save and don't publish" onClick={() => submit(false)} />
						)}
						<Button colorPalette={color} variant="destructive" label="Cancel" onClick={() => setOpen(false)} />
					</ModalFooter>
				</DialogContainer>
			</Modal>
		)
	);
}

interface ConfirmDeleteModalProps {
	open: boolean;
	overlayObj: ConfirmDeleteInterface | null;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function ConfirmDeleteModal(props: ConfirmDeleteModalProps) {
	const { open, overlayObj, setOpen } = props;

	const color = useTheme();

	function submit() {
		if (!overlayObj) return;
		overlayObj.cb();
		setOpen(false);
	}

	return (
		overlayObj && (
			<Modal open={open} colorPalette={color} setOpen={setOpen}>
				<DialogContainer style={{ zIndex: 10 }}>
					<ModalTitleContainer>
						<ModalTitle>Are you sure you want to delete {overlayObj.docType}</ModalTitle>
						<FiX onClick={() => setOpen(false)} size={24} />
					</ModalTitleContainer>
					<ModalFooter>
						<Button colorPalette={color} variant="outlined" label={"Delete"} onClick={submit} />
						<Button colorPalette={color} variant="destructive" label="Cancel" onClick={() => setOpen(false)} />
					</ModalFooter>
				</DialogContainer>
			</Modal>
		)
	);
}

export default EditFormTemplate;
